import { CustomException } from '@tencent/ppd-base-libs';

/**
 * 赋值数组新值
 * @param array 数组
 * @param data 新数据
 */
export const assignArray = <T = any>(array: T[], data: T[]) => {
  array.splice(0, array.length);
  array.push(...data);
};

/**
 * 根据某一字段作为外键关联两个数组，类似sql中left join
 * @param targetArray 目标数组（左表）
 * @param joinArray 关联数组（右表）
 * @param key 主键字段
 * @returns 关联合并后的数组
 */
export const leftJoin = <T1 = any, T2 = any>(
  targetArray: T1[],
  joinArray: T2[],
  key: string): (T1 & T2)[] => (targetArray || []).map((targetItem) => {
    const targetJoinItem = (joinArray || []).find(joinItem => joinItem[key] === targetItem[key]) as T2;
    return { ...targetItem, ...targetJoinItem } ;
  });
/**
 * 获取日期字符串:'2021/01/01'
 * @param date 日期
 */
export const getDayDateStr = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}/${month}/${day}`;
};

/**
 * 获取promise实例及句柄
 */
export function getPromis<T = any>() {
  let resolve; let reject;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    resolve: resolve as (value: T | PromiseLike<T>) => void,
    reject: reject as (reason?: any) => void,
    promise,
  };
}

/**
 * 等待
 * @param time 时间(秒)
 */
export function sleep(time: number) {
  return new Promise((res) => {
    setTimeout(() => {
      res(undefined);
    }, time * 1000);
  });
}
type ReturnError = any;
export function to<T>(promise: Promise<T>): Promise<[null, T] | [ReturnError, T]> {
  return promise.then(data => [null, data] as [null, T])
    .catch(err => [err, null] as [ReturnError, unknown] as [ReturnError, T]);
}

/**
 * Promise.allSettled 安全版
 * @param promiseList Promise实例及其信息
 */
export async function checkPromiseList<T extends Promise<any>>(promiseList: {
  promise: T,
  name?: string;
  isRequired?: boolean;
}[]) {
  const pList = promiseList.map(item => item.promise);
  const reuslts = await allSettled(pList);
  return reuslts.map((result, index) => {
    const { status } = result;
    if (status === 'fulfilled') {
      return result.value;
    }
    const { name = 'Common', isRequired = true } = promiseList[index];
    const error = result.reason;
    new CustomException(error, `Required${name}PromiseFailed`);
    if (isRequired) throw error;
    return error;
  });
}

/**
 * 两数之前随机整数(左闭右闭)
 */
export function randomBetween(min: number, max: number) {
  return Math.round(Math.random() * (max - min)) + min;
}

/**
 * 获取日期字符串，最小单位为天
 * @param date 日期
 */
export const getDayDate = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${year}/${month}/${day}`;
};
/**
 * 缓存图片列表
 * @param imgList 图片列表
 */
export function cacheImg(imgList: string[]) {
  imgList.forEach((img) => {
    (new Image).src = img;
  });
}
/**
 * 获取背景图片行内style
 * @param backgroundImg 背景图
 */
export function getBGStyle(backgroundImg: string) {
  return {
    backgroundImage: `url("${backgroundImg}")`,
  };
}

type AllSettledRes = ({
  status: 'fulfilled',
  value: any
} | {
  status: 'rejected',
  reason: any
})[];

export function allSettled(promises: Promise<any>[]): Promise<AllSettledRes> {
  // @ts-ignore
  return Promise.all(promises.map(p => p
    .then(value => ({
      status: 'fulfilled', value,
    }))
    .catch(reason => ({
      status: 'rejected', reason,
    }))));
}

function promiseTimeout(ms, throwOnTimeout = false, reason = 'TIME_OUT_REASON') {
  return new Promise((resolve, reject) => {
    if (throwOnTimeout) setTimeout(() => reject(reason), ms);
    else setTimeout(resolve, ms);
  });
}

export async function handleTimeout({ promise, timeoutMs = 3000 }) {
  let res: any;
  let isReady = false;
  let err: any;
  let isTimeout = false;
  const TIME_OUT_REASON = 'TIME_OUT_REASON';

  const timeoutPromise = promiseTimeout(timeoutMs, true, TIME_OUT_REASON);

  await Promise.race([promise, timeoutPromise]).then((response) => {
    res = response;
    isReady = true;
  })
    .catch((error) => {
      if (error === TIME_OUT_REASON) {
        isTimeout = true;
      } else {
        err = error;
      }
      isReady = true;
    });
  return {
    res,
    isReady,
    isTimeout,
    err,
  };
}
